import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Certificate } from '@app/_models/certificate';
import { ReportSearch } from '@app/_models/reportSearch';

interface CacheEntry {
  searchParams: ReportSearch;
  results: Certificate[];
  timestamp: number;
  cacheKey: string;
}

interface StatusChange {
  certificateId: string;
  certificateNumber: string;
  oldStatus: string;
  newStatus: string;
  timestamp: number;
}

/**
 * Certificate Cache Service
 * Provides smart caching for certificate search results with:
 * - Filter persistence
 * - Status change tracking
 * - Stale data detection
 * - Cache invalidation on user actions
 */
@Injectable({
  providedIn: 'root'
})
export class CertificateCacheService {
  private cache: Map<string, CacheEntry> = new Map();
  private statusChanges: StatusChange[] = [];
  private readonly STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes
  private readonly MAX_CACHE_ENTRIES = 10;
  private readonly MAX_STATUS_CHANGES = 50;

  // Observable for stale data notifications
  private staleDataSubject = new BehaviorSubject<{ isStale: boolean; message: string }>({
    isStale: false,
    message: ''
  });
  public staleData$: Observable<{ isStale: boolean; message: string }> = this.staleDataSubject.asObservable();

  constructor() {
    // Load persisted cache from sessionStorage on initialization
    this.loadCacheFromStorage();
  }

  /**
   * Generate cache key from search parameters
   */
  private generateCacheKey(searchParams: ReportSearch): string {
    const keyParts = [
      searchParams.certificateNumber || '',
      searchParams.chasisNumber || '',
      searchParams.firstName || '',
      searchParams.lastName || '',
      searchParams.userName || '',
      searchParams.contactNumber || '',
      searchParams.issueDateFrom || '',
      searchParams.issueDateTo || '',
      searchParams.userId || '',
      (searchParams.statuses || []).join(',')
    ];
    return btoa(keyParts.join('|')).replace(/[/+=]/g, '');
  }

  /**
   * Get cached results if available and not stale
   */
  getCachedResults(searchParams: ReportSearch): Certificate[] | null {
    // Normalize search params to ensure consistent cache keys
    const normalizedParams: ReportSearch = {
      ...searchParams,
      certificateNumber: searchParams.certificateNumber || '',
      chasisNumber: searchParams.chasisNumber || '',
      firstName: searchParams.firstName || '',
      lastName: searchParams.lastName || '',
      userName: searchParams.userName || '',
      contactNumber: searchParams.contactNumber || '',
      issueDateFrom: searchParams.issueDateFrom || '',
      issueDateTo: searchParams.issueDateTo || '',
      userId: searchParams.userId || '',
      statuses: searchParams.statuses || []
    };
    
    const cacheKey = this.generateCacheKey(normalizedParams);
    console.log('Cache lookup - Key:', cacheKey, 'Search params:', JSON.stringify(normalizedParams));
    console.log('Available cache keys:', Array.from(this.cache.keys()));
    
    const entry = this.cache.get(cacheKey);

    if (!entry) {
      console.log('Cache miss - No entry found for key:', cacheKey);
      // Try to find a similar key (for debugging)
      this.cache.forEach((value, key) => {
        console.log('  Cache entry key:', key, 'params:', JSON.stringify(value.searchParams));
      });
      return null;
    }

    console.log('Cache hit - Found entry with', entry.results.length, 'results, age:', Date.now() - entry.timestamp, 'ms');

    // Check if cache is stale
    const age = Date.now() - entry.timestamp;
    if (age > this.STALE_THRESHOLD_MS) {
      // Mark as stale but still return cached data
      console.log('Cache is stale but returning cached data');
      this.staleDataSubject.next({
        isStale: true,
        message: 'Data may be stale. Click Search again to refresh.'
      });
      return entry.results;
    }

    // Check if any certificates in cache have status changes
    const hasStatusChanges = this.hasRelevantStatusChanges(entry.results);
    if (hasStatusChanges) {
      // Invalidate this cache entry
      console.log('Cache invalidated due to status changes');
      this.cache.delete(cacheKey);
      this.saveCacheToStorage();
      return null;
    }

    // Cache is valid
    console.log('Cache is valid, returning', entry.results.length, 'results');
    this.staleDataSubject.next({
      isStale: false,
      message: ''
    });
    return entry.results;
  }

  /**
   * Check if any certificates in the results have had status changes
   * Returns false now since we update cache instead of invalidating
   */
  private hasRelevantStatusChanges(certificates: Certificate[]): boolean {
    // We now update cache entries instead of invalidating them
    // So we don't need to check for status changes here
    // The cache will be updated when status changes occur
    return false;
  }

  /**
   * Cache search results
   */
  cacheResults(searchParams: ReportSearch, results: Certificate[]): void {
    // Normalize search params to ensure consistent cache keys
    const normalizedParams: ReportSearch = {
      ...searchParams,
      certificateNumber: searchParams.certificateNumber || '',
      chasisNumber: searchParams.chasisNumber || '',
      firstName: searchParams.firstName || '',
      lastName: searchParams.lastName || '',
      userName: searchParams.userName || '',
      contactNumber: searchParams.contactNumber || '',
      issueDateFrom: searchParams.issueDateFrom || '',
      issueDateTo: searchParams.issueDateTo || '',
      userId: searchParams.userId || '',
      statuses: searchParams.statuses || []
    };
    
    const cacheKey = this.generateCacheKey(normalizedParams);
    console.log('Caching results - Key:', cacheKey, 'Search params:', JSON.stringify(normalizedParams), 'Results:', results.length);
    
    // Limit cache size
    if (this.cache.size >= this.MAX_CACHE_ENTRIES) {
      // Remove oldest entry
      const oldestKey = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
      this.cache.delete(oldestKey);
    }

    const entry: CacheEntry = {
      searchParams: normalizedParams,
      results: [...results],
      timestamp: Date.now(),
      cacheKey
    };

    this.cache.set(cacheKey, entry);
    this.saveCacheToStorage();
    console.log('Cache saved. Total entries:', this.cache.size);
    
    // Clear stale data notification
    this.staleDataSubject.next({
      isStale: false,
      message: ''
    });
  }

  /**
   * Track certificate status change from user action
   * Updates cached results instead of invalidating entire cache
   */
  trackStatusChange(
    certificateId: string,
    certificateNumber: string,
    oldStatus: string,
    newStatus: string
  ): void {
    const change: StatusChange = {
      certificateId,
      certificateNumber,
      oldStatus,
      newStatus,
      timestamp: Date.now()
    };

    this.statusChanges.push(change);

    // Limit status changes history
    if (this.statusChanges.length > this.MAX_STATUS_CHANGES) {
      this.statusChanges.shift();
    }

    // Update cached results instead of invalidating
    this.updateCacheForCertificate(certificateNumber, newStatus);
    this.saveStatusChangesToStorage();
  }

  /**
   * Update cached certificate with complete data (for endorsements with demographic changes)
   * Replaces the entire certificate object in cache with the new updated certificate
   */
  updateCachedCertificateData(updatedCertificate: Certificate): void {
    this.cache.forEach((entry, key) => {
      const certificateIndex = entry.results.findIndex(
        cert => cert.certificateNumber === updatedCertificate.certificateNumber
      );
      if (certificateIndex !== -1) {
        // Replace the entire certificate with updated data (maintains object reference)
        Object.assign(entry.results[certificateIndex], updatedCertificate);
        // Update timestamp to mark cache as recently updated
        entry.timestamp = Date.now();
        console.log('✅ Updated cached certificate data:', updatedCertificate.certificateNumber);
      }
    });
    this.saveCacheToStorage();
  }

  /**
   * Update cached results for a specific certificate instead of invalidating
   */
  private updateCacheForCertificate(certificateNumber: string, newStatus: string): void {
    this.cache.forEach((entry, key) => {
      const certificateIndex = entry.results.findIndex(
        cert => cert.certificateNumber === certificateNumber
      );
      if (certificateIndex !== -1) {
        // Update the certificate status in cached results
        entry.results[certificateIndex].certificateStatus = newStatus;
        // Update timestamp to mark cache as recently updated
        entry.timestamp = Date.now();
      }
    });
    this.saveCacheToStorage();
  }

  /**
   * Invalidate cache entries containing a specific certificate (used for major changes)
   */
  private invalidateCacheForCertificate(certificateNumber: string): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((entry, key) => {
      const hasCertificate = entry.results.some(
        cert => cert.certificateNumber === certificateNumber
      );
      if (hasCertificate) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
    this.saveCacheToStorage();
  }

  /**
   * Get persisted search filters from sessionStorage
   * Returns null if filters are expired (older than 1 hour) or URL has changed
   */
  getPersistedFilters(): ReportSearch | null {
    try {
      const stored = sessionStorage.getItem('certificate-search-filters');
      const storedTimestamp = sessionStorage.getItem('certificate-search-filters-timestamp');
      const storedUrl = sessionStorage.getItem('certificate-search-filters-url');
      
      if (stored && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const now = Date.now();
        const age = now - timestamp;
        const ONE_HOUR_MS = 60 * 60 * 1000; // 1 hour

        // If URL has changed since filters were stored, treat filters as invalid
        try {
          const currentUrl = window.location.pathname + window.location.search;
          if (storedUrl && storedUrl !== currentUrl) {
            console.log('🔁 URL changed since filters were stored. Invalidating persisted filters.', {
              storedUrl,
              currentUrl
            });
            this.clearPersistedFilters();
            return null;
          }
        } catch (e) {
          // In non-browser environments, window may not be available; ignore URL check
        }
        
        if (age > ONE_HOUR_MS) {
          // Filters expired, clear them
          console.log('⏰ Persisted filters expired (older than 1 hour), clearing...');
          this.clearPersistedFilters();
          return null;
        }
        
        const filters = JSON.parse(stored) as ReportSearch;
        console.log('📥 Loaded persisted filters from storage (age:', Math.round(age / 1000 / 60), 'minutes):', JSON.stringify(filters));
        return filters;
      }
    } catch (error) {
      console.error('Error loading persisted filters:', error);
    }
    return null;
  }

  /**
   * Persist search filters to sessionStorage with timestamp
   * Filters expire after 1 hour
   */
  persistFilters(searchParams: ReportSearch): void {
    try {
      // Capture current URL to bind this cache to the route that created it
      let currentUrl = '';
      try {
        currentUrl = window.location.pathname + window.location.search;
      } catch (e) {
        // Ignore if window is not available (e.g., during server-side rendering)
      }

      // Ensure all important fields are included, especially dates
      // Use Partial to avoid TypeScript errors since ReportSearch has many optional fields
      const filtersToStore: Partial<ReportSearch> = {
        ...searchParams,
        certificateNumber: searchParams.certificateNumber || '',
        chasisNumber: searchParams.chasisNumber || '',
        firstName: searchParams.firstName || '',
        lastName: searchParams.lastName || '',
        userName: searchParams.userName || '',
        contactNumber: searchParams.contactNumber || '',
        issueDateFrom: searchParams.issueDateFrom || '', // Ensure dates are stored
        issueDateTo: searchParams.issueDateTo || '', // Ensure dates are stored
        userId: searchParams.userId || '',
        startDateOrIssueDate: searchParams.startDateOrIssueDate || 'issuedate',
        statuses: searchParams.statuses || []
      };
      const timestamp = Date.now();
      sessionStorage.setItem('certificate-search-filters', JSON.stringify(filtersToStore));
      sessionStorage.setItem('certificate-search-filters-timestamp', timestamp.toString());
      if (currentUrl) {
        sessionStorage.setItem('certificate-search-filters-url', currentUrl);
      }
      console.log('💾 Persisted filters to storage with timestamp:', JSON.stringify(filtersToStore));
    } catch (error) {
      console.error('Error persisting filters:', error);
    }
  }

  /**
   * Clear persisted filters
   */
  clearPersistedFilters(): void {
    try {
      sessionStorage.removeItem('certificate-search-filters');
      sessionStorage.removeItem('certificate-search-filters-timestamp');
      sessionStorage.removeItem('certificate-search-filters-url');
    } catch (error) {
      console.error('Error clearing persisted filters:', error);
    }
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.statusChanges = [];
    this.saveCacheToStorage();
    this.saveStatusChangesToStorage();
  }

  /**
   * Check if data is stale for current search
   */
  isStale(searchParams: ReportSearch): boolean {
    const cacheKey = this.generateCacheKey(searchParams);
    const entry = this.cache.get(cacheKey);
    
    if (!entry) {
      return false;
    }

    const age = Date.now() - entry.timestamp;
    return age > this.STALE_THRESHOLD_MS;
  }

  /**
   * Save cache to sessionStorage
   */
  private saveCacheToStorage(): void {
    try {
      const cacheData = Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        searchParams: entry.searchParams,
        results: entry.results,
        timestamp: entry.timestamp
      }));
      sessionStorage.setItem('certificate-cache', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving cache to storage:', error);
    }
  }

  /**
   * Load cache from sessionStorage
   */
  private loadCacheFromStorage(): void {
    try {
      const stored = sessionStorage.getItem('certificate-cache');
      if (stored) {
        const cacheData = JSON.parse(stored);
        cacheData.forEach((item: any) => {
          // Only load cache entries that are not stale
          const age = Date.now() - item.timestamp;
          if (age < this.STALE_THRESHOLD_MS) {
            this.cache.set(item.key, {
              searchParams: item.searchParams,
              results: item.results,
              timestamp: item.timestamp,
              cacheKey: item.key
            });
          }
        });
      }
    } catch (error) {
      console.error('Error loading cache from storage:', error);
    }

    // Load status changes
    this.loadStatusChangesFromStorage();
  }

  /**
   * Save status changes to sessionStorage
   */
  private saveStatusChangesToStorage(): void {
    try {
      sessionStorage.setItem('certificate-status-changes', JSON.stringify(this.statusChanges));
    } catch (error) {
      console.error('Error saving status changes to storage:', error);
    }
  }

  /**
   * Load status changes from sessionStorage
   */
  private loadStatusChangesFromStorage(): void {
    try {
      const stored = sessionStorage.getItem('certificate-status-changes');
      if (stored) {
        const changes = JSON.parse(stored) as StatusChange[];
        // Only keep changes from last 10 minutes
        const tenMinutesAgo = Date.now() - this.STALE_THRESHOLD_MS;
        this.statusChanges = changes.filter(change => change.timestamp > tenMinutesAgo);
      }
    } catch (error) {
      console.error('Error loading status changes from storage:', error);
    }
  }
}

